<template>
  <main class="asset-dashboard-page">
    <!-- Period filter -->
    <div class="toolbar">
      <el-tabs v-model="activePeriod" @tab-click="fetchAll">
        <el-tab-pane label="今日" name="day" />
        <el-tab-pane label="近 7 天" name="week" />
        <el-tab-pane label="本月" name="month" />
      </el-tabs>
    </div>

    <!-- KPI 卡片 -->
    <section class="kpis">
      <div class="card" v-for="card in kpiCards" :key="card.label">
        <div class="card-head">
          <span>{{ card.label }}</span>
          <span class="live-tag">LIVE</span>
        </div>
        <div class="metric">
          {{ card.value }}
          <small v-if="card.unit">{{ card.unit }}</small>
        </div>
        <div class="delta muted-text">
          <small>{{ card.sub || '—' }}</small>
        </div>
      </div>
    </section>

    <!-- 调用趋势 + AVS 排行 -->
    <section class="grid two">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">调用趋势</h3>
          <span class="panel-sub">{{ periodLabel }}</span>
        </div>
        <div ref="trendChart" class="chart-container" />
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">AVS 工具排行</h3>
          <span class="panel-sub">本月</span>
        </div>
        <div class="depth">
          <div class="depth-row" v-for="row in avsList" :key="row.agent_name">
            <b>{{ row.agent_name }}</b>
            <div class="depth-bar"><span :style="{ width: depthPct(row.avs_score, avsList, 'avs_score') + '%' }"></span></div>
            <strong>{{ row.avs_score }}</strong>
          </div>
          <div v-if="!avsList.length" class="empty-state">暂无数据</div>
        </div>
      </div>
    </section>

    <!-- Rf 排行 + 部门排行 -->
    <section class="grid two">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">Rf 工具排行</h3>
          <span class="panel-sub">本月</span>
        </div>
        <div class="depth">
          <div class="depth-row" v-for="row in rfList" :key="row.agent_name">
            <b>{{ row.agent_name }}</b>
            <div class="depth-bar"><span :style="{ width: depthPct(row.weighted_rf, rfList, 'weighted_rf') + '%' }"></span></div>
            <strong>{{ row.weighted_rf }}</strong>
          </div>
          <div v-if="!rfList.length" class="empty-state">暂无数据</div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">部门调用排行</h3>
          <span class="panel-sub">本月</span>
        </div>
        <div class="depth">
          <div class="depth-row" v-for="row in deptList" :key="row.dept_name">
            <b>{{ row.dept_name }}</b>
            <div class="depth-bar"><span :style="{ width: depthPct(row.call_count, deptList, 'call_count') + '%' }"></span></div>
            <strong>{{ $formatNumber(row.call_count) }}</strong>
          </div>
          <div v-if="!deptList.length" class="empty-state">暂无数据</div>
        </div>
      </div>
    </section>

    <!-- 成功率排行 -->
    <section class="panel" style="margin-top:16px">
      <div class="panel-head">
        <h3 class="panel-title">成功率排行（倒数）</h3>
        <span class="panel-sub">本月，仅显示调用 >= 5 次的工具</span>
      </div>
      <div class="depth">
        <div class="depth-row" v-for="row in rateList" :key="row.agent_name">
          <b>{{ row.agent_name }}</b>
          <div class="depth-bar"><span :style="{ width: depthPct((row.success_rate || 0) * 100, rateList, 'ratePct') + '%' }"></span></div>
          <strong>{{ ((row.success_rate || 0) * 100).toFixed(2) }}%</strong>
        </div>
        <div v-if="!rateList.length" class="empty-state">暂无数据</div>
      </div>
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'AssetDashboardPage',
  data() {
    return {
      activePeriod: 'day',
      kpiCards: [
        { label: '工具总数', value: '0', unit: '个', sub: '已注册工具' },
        { label: '活跃工具', value: '0', unit: '个', sub: '状态为 active' },
        { label: '本月调用', value: '0', unit: '次', sub: '—' },
        { label: '有效调用', value: '0', unit: '次', sub: '状态码=200/499' },
        { label: '成功率', value: '0', unit: '%', sub: '—' }
      ],
      avsList: [],
      rfList: [],
      deptList: [],
      rateList: [],
      trendChart: null
    }
  },
  computed: {
    periodLabel() {
      const map = { day: '今日', week: '近 7 天', month: '本月' }
      return map[this.activePeriod] || '今日'
    }
  },
  mounted() {
    this.fetchAll()
  },
  beforeDestroy() {
    if (this.trendChart) this.trendChart.dispose()
  },
  methods: {
    async fetchAll() {
      await Promise.all([
        this.fetchKpi(),
        this.fetchCallTrend(),
        this.fetchTopAvs(),
        this.fetchTopRf(),
        this.fetchTopDept(),
        this.fetchTopSuccessRate()
      ])
    },
    async fetchKpi() {
      try {
        const data = await this.$http.get('/admin/asset-dashboard/kpi', { params: { period: this.activePeriod } })
        if (!data) return
        this.kpiCards[0].value = this.$formatNumber(data.totalAgents)
        this.kpiCards[1].value = this.$formatNumber(data.activeAgents)
        this.kpiCards[2].value = this.$formatNumber(data.totalCalls)
        this.kpiCards[3].value = this.$formatNumber(data.validCalls)
        this.kpiCards[4].value = data.successRate != null ? (data.successRate * 100).toFixed(2) : '0'
      } catch (e) {
        console.error('Failed to fetch KPI:', e)
      }
    },
    async fetchCallTrend() {
      try {
        const data = await this.$http.get('/admin/asset-dashboard/call-trend', {
          params: { period: this.activePeriod }
        }) || []
        this.$nextTick(() => this.renderTrendChart(data))
      } catch (e) {
        console.error('Failed to fetch trend:', e)
      }
    },
    async fetchTopAvs() {
      try {
        this.avsList = await this.$http.get('/admin/asset-dashboard/top-avs') || []
      } catch (e) {
        console.error('Failed to fetch AVS:', e)
      }
    },
    async fetchTopRf() {
      try {
        this.rfList = await this.$http.get('/admin/asset-dashboard/top-rf') || []
      } catch (e) {
        console.error('Failed to fetch RF:', e)
      }
    },
    async fetchTopDept() {
      try {
        this.deptList = await this.$http.get('/admin/asset-dashboard/top-dept') || []
      } catch (e) {
        console.error('Failed to fetch dept:', e)
      }
    },
    async fetchTopSuccessRate() {
      try {
        this.rateList = await this.$http.get('/admin/asset-dashboard/top-success-rate') || []
      } catch (e) {
        console.error('Failed to fetch rate:', e)
      }
    },
    depthPct(val, list, key) {
      const max = Math.max(...list.map(r => Number(r[key] || 0)), 1)
      return (val / max) * 100
    },
    renderTrendChart(data) {
      const el = this.$refs.trendChart
      if (!el) return
      if (this.trendChart) this.trendChart.dispose()
      const chart = echarts.init(el)
      this.trendChart = chart

      const dates = data.map(d => d.date)
      const calls = data.map(d => d.call_count)

      chart.setOption({
        tooltip: { trigger: 'axis', backgroundColor: 'rgba(20,25,40,0.9)', borderColor: 'rgba(133,164,255,0.2)', textStyle: { color: '#c8d0e0', fontSize: 12 } },
        grid: { left: 60, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: 'rgba(133,164,255,0.15)' } }, axisLabel: { color: '#9aaac1', fontSize: 11, rotate: dates.length > 20 ? 45 : 0 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: 'rgba(133,164,255,0.08)' } }, axisLabel: { color: '#9aaac1', fontSize: 11 } },
        series: [{
          type: 'line', data: calls, smooth: true, symbol: 'none',
          lineStyle: { width: 2, color: '#4f86ff' },
          areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(79,134,255,0.28)' }, { offset: 1, color: 'rgba(79,134,255,0)' }]) }
        }]
      })
    }
  }
}
</script>

<style scoped>
.asset-dashboard-page {
  padding: 0 24px 76px;
}

.asset-dashboard-page ::v-deep .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

.asset-dashboard-page ::v-deep .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

.asset-dashboard-page ::v-deep .el-tabs__item:hover {
  color: var(--text);
}

.asset-dashboard-page ::v-deep .el-tabs__item.is-active {
  color: var(--text);
}

.asset-dashboard-page ::v-deep .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

.asset-dashboard-page ::v-deep .el-tabs__nav-wrap::after {
  background: transparent;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.card {
  min-height: 100px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
  position: relative;
  overflow: hidden;
}

.card::after {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(50, 200, 245, 0.78), transparent 70%);
  opacity: 0.5;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 12px;
  font-weight: 760;
}

.live-tag {
  color: var(--cyan);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.metric {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  font-size: clamp(18px, 2vw, 24px);
  line-height: 1.05;
  font-weight: 900;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--text);
}

.metric small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.delta {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 850;
}

.muted-text {
  color: var(--muted);
}

.panel {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 880;
  color: var(--text);
}

.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.grid {
  display: grid;
  gap: 16px;
  margin-top: 16px;
}

.grid.two {
  grid-template-columns: 1fr 1fr;
}

.chart-container {
  width: 100%;
  height: 240px;
}

.depth {
  display: grid;
  gap: 12px;
}

.depth-row {
  display: grid;
  grid-template-columns: 110px 1fr 90px;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--text);
}

.depth-row b {
  font-weight: 800;
  color: #c6d4e9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.depth-bar {
  height: 10px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.depth-bar span {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, var(--cyan), var(--blue));
  box-shadow: 0 0 12px rgba(50, 200, 245, 0.3);
  transition: width 0.4s ease;
}

.depth-row strong {
  text-align: right;
  font-weight: 850;
  color: var(--text);
}

.empty-state {
  text-align: center;
  color: var(--muted);
  font-size: 13px;
  padding: 20px 0;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  flex-wrap: wrap;
}

@media (max-width: 1200px) {
  .kpis {
    grid-template-columns: repeat(3, 1fr);
  }
  .grid.two {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .asset-dashboard-page {
    padding: 0 12px 28px;
  }
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>